import { LockClosedIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import Avatar from '@/components/Avatar';

export interface SidebarGroup {
  id: string;
  name: string;
  orgId: string;
  orgName: string;
  imageUrl?: string | null;
}

/** Count bubble on a group row. Urgent = needs the user's attention (red). */
export interface RailBadge {
  count: number;
  urgent: boolean;
}

interface Props {
  group: SidebarGroup;
  selected: boolean;
  onSelect: (group: SidebarGroup) => void;
  badge?: RailBadge;
  /** Org is paused — dim the row and show a lock glyph, but stay clickable. */
  locked?: boolean;
}

/** Group row in a cross-org rail tree (PrayerGroupListItem pattern). */
export default function GroupRailListItem({ group, selected, onSelect, badge, locked }: Props) {
  return (
    <button
      onClick={() => onSelect(group)}
      className={clsx(
        'flex w-full items-center gap-3 rounded-lg py-2 pl-6 pr-3 text-left transition',
        selected ? 'bg-flock-navy-100' : 'hover:bg-flock-navy-50'
      )}
    >
      <div className={clsx(locked && 'opacity-50')}>
        <Avatar
          src={group.imageUrl}
          name={group.name}
          sizeClass="h-9 w-9"
          textClass="text-xs"
          bgClass="bg-flock-navy-500"
        />
      </div>
      <span
        className={clsx(
          'min-w-0 flex-1 truncate text-sm',
          locked
            ? 'font-medium text-flock-navy-400'
            : selected
              ? 'font-semibold text-flock-navy-900'
              : 'font-medium text-flock-navy-800'
        )}
      >
        {group.name}
      </span>
      {locked && (
        <LockClosedIcon className="h-3.5 w-3.5 shrink-0 text-flock-navy-400" aria-hidden="true" />
      )}
      {!locked && badge && badge.count > 0 && (
        <span
          className={clsx(
            'flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-semibold',
            badge.urgent ? 'bg-[#E63946] text-white' : 'bg-flock-gray-300 text-flock-navy-700'
          )}
        >
          {badge.count > 99 ? '99+' : badge.count}
        </span>
      )}
    </button>
  );
}
